// Shared feeding constants (kept in sync with backend server.py FEED_* lists).
export const FEED_CATEGORIES = ["Fieno", "Mangime", "Cereale", "Wafer", "Integratore", "Altro"];
export const FEED_BASE_UNITS = ["kg", "g", "q", "t", "L", "ml", "unità"];
export const FEED_PURCHASE_UNITS = ["kg", "g", "q", "t", "L", "ml", "ballina", "balla", "ballone", "rotoballa", "sacco", "confezione", "unità"];
export const DEFAULT_CATEGORY_ORDER = ["Fieno", "Mangime", "Wafer", "Cereale", "Integratore", "Altro"];

// Controlled pastel palette (token -> {bg, border, label}). Kept in sync with backend FEED_COLOR_TOKENS.
export const FEED_COLOR_TOKENS = ["giallo", "ambra", "pesca", "rosa", "lavanda", "blu", "azzurro", "salvia", "verde", "grigio"];
export const FEED_COLOR_MAP = {
  giallo: { bg: "#FBF3D6", border: "#EFE2B0", label: "Giallo" },
  ambra: { bg: "#FBEBD6", border: "#F0D9B4", label: "Ambra" },
  pesca: { bg: "#FBE3DA", border: "#F1CDBF", label: "Pesca" },
  rosa: { bg: "#F9DEE6", border: "#EEC3D0", label: "Rosa" },
  lavanda: { bg: "#EAE4F6", border: "#D5CAED", label: "Lavanda" },
  blu: { bg: "#DCE6F6", border: "#C0D2ED", label: "Blu" },
  azzurro: { bg: "#D8ECF1", border: "#BBDCE6", label: "Azzurro" },
  salvia: { bg: "#E1EBDD", border: "#C8DBC1", label: "Salvia" },
  verde: { bg: "#DDECD9", border: "#C2DDBA", label: "Verde" },
  grigio: { bg: "#EAE7E1", border: "#D6D1C6", label: "Grigio" },
};
export const feedColorStyle = (token) => { const c = FEED_COLOR_MAP[token]; return c ? { backgroundColor: c.bg, borderColor: c.border } : null; };

export const nfIT = (n) => Number(n).toLocaleString("it-IT", { maximumFractionDigits: 3 });

export const feedConversionText = (it) => {
  const pu = it.purchase_unit, bu = it.base_unit, ps = Number(it.pack_size);
  if (pu === bu && ps === 1) return `Consumo in ${bu}`;
  return `1 ${pu} = ${nfIT(ps)} ${bu}`;
};

export const todayISO = () => new Date().toISOString().slice(0, 10);
export const fmtDateIT = (d) => {
  if (!d) return "—";
  const [y, m, dd] = String(d).slice(0, 10).split("-");
  return (y && m && dd) ? `${dd}/${m}/${y}` : String(d);
};
// null = no data ("nessun dato") ; 0 = intentional zero. Distinct on purpose.
export const fmtQty = (q, unit) => {
  if (q === null || q === undefined) return "nessun dato";
  return `${nfIT(q)} ${unit || ""}`.trim();
};
// Redundant "Fieno / Fieno" collapses to just the category label.
export const categoryOf = (item) => item?.category || "Altro";

// --- Universal Profenda sorting rule: meal-slot order -> category order -> item name ---
export function makeLineSorter(itemsMap, categoryOrder) {
  const rank = (c) => { const i = (categoryOrder || DEFAULT_CATEGORY_ORDER).indexOf(c); return i < 0 ? 999 : i; };
  return (a, b) => {
    const ia = itemsMap[a.feed_item_id], ib = itemsMap[b.feed_item_id];
    const ca = rank(ia?.category), cb = rank(ib?.category);
    if (ca !== cb) return ca - cb;
    return (ia?.name || "").localeCompare(ib?.name || "", "it");
  };
}
// Returns [{ slot, lines }] for slots that have lines, in slot order, lines sorted by rule.
export function groupRationBySlot(lines, activeSlots, itemsMap, categoryOrder) {
  const sorter = makeLineSorter(itemsMap, categoryOrder);
  const bySlot = {};
  (lines || []).forEach((l) => { (bySlot[l.meal_slot_id] = bySlot[l.meal_slot_id] || []).push(l); });
  return (activeSlots || []).filter((s) => bySlot[s.id]).map((s) => ({ slot: s, lines: [...bySlot[s.id]].sort(sorter) }));
}
// Categories actually used at least once, in configured order (for stable-position grid columns).
export function usedCategoriesInOrder(lines, itemsMap, categoryOrder) {
  const used = new Set((lines || []).map((l) => itemsMap[l.feed_item_id]?.category).filter(Boolean));
  return (categoryOrder || DEFAULT_CATEGORY_ORDER).filter((c) => used.has(c));
}

// --- Editor state <-> feed_rations lines conversion (shared by ProfendaDetail and bulk editor) ---
export const uidLite = () => Math.random().toString(36).slice(2);

// Build editor groups [{_id, slot_id, items:[{_id, feed_item_id, quantity, note}]}] from ration lines,
// grouped by meal slot and ordered by configured slot order.
export function linesToGroups(lines, activeSlots) {
  const bySlot = {};
  (lines || []).forEach((l) => { (bySlot[l.meal_slot_id] = bySlot[l.meal_slot_id] || []).push(l); });
  return (activeSlots || []).filter((s) => bySlot[s.id]).map((s) => ({
    _id: uidLite(), slot_id: s.id,
    items: bySlot[s.id].map((l) => ({ _id: uidLite(), feed_item_id: l.feed_item_id,
      quantity: (l.quantity === null || l.quantity === undefined) ? "" : String(l.quantity), note: l.note || "" })),
  }));
}

// Flatten editor groups to feed_rations lines. Empty rows are dropped; a row with qty/note but no
// food selected is an error. Empty groups produce no operational content. null != 0 preserved.
export function groupsToLines(groups) {
  const lines = [];
  for (const g of groups || []) {
    for (const it of g.items) {
      if (!it.feed_item_id) {
        if (it.quantity !== "" || (it.note || "").trim()) return { error: "Seleziona l'alimento in ogni riga compilata" };
        continue;
      }
      lines.push({ meal_slot_id: g.slot_id, feed_item_id: it.feed_item_id,
        quantity: it.quantity === "" ? null : Number(it.quantity), note: it.note || null });
    }
  }
  return { lines };
}

// --- Profenda Coverage V1: three semantic states per meal slot on the current ration ---
export const SLOT_STATE = { CONFIGURED: "configured", EMPTY: "intentionally_empty", NONE: "non_configured" };
export function slotState(ration, slotId) {
  if (!ration) return SLOT_STATE.NONE;
  if ((ration.lines || []).some((l) => l.meal_slot_id === slotId)) return SLOT_STATE.CONFIGURED;
  if ((ration.empty_slots || []).includes(slotId)) return SLOT_STATE.EMPTY;
  return SLOT_STATE.NONE;
}
// A ration counts as a real profenda only if it has at least one line OR one declared-empty slot.
// A "current" doc with no lines and no empty_slots is a ghost (e.g. created empty) and must be
// derived as "no profenda" — NOT rendered as every slot ⚠ non_configured. Display-only rule.
export const hasProfenda = (ration) => !!ration && (((ration.lines || []).length > 0) || ((ration.empty_slots || []).length > 0));

// --- Feeding responsibility (per-horse). setting = {managed_by, scope, owner_slots} or undefined.
// A slot managed by the owner is OUT of the stable's Coverage & operational list for that horse.
// scope "all" covers every slot (incl. future ones); "some" only the enumerated owner_slots.
export const slotOwnerManaged = (setting, slotId) => {
  if (!setting || setting.managed_by !== "proprietario") return false;
  if (setting.scope === "all") return true;
  return (setting.owner_slots || []).includes(slotId);
};
export const isFullyOwnerManaged = (setting) => !!setting && setting.managed_by === "proprietario" && setting.scope === "all";

// Names of active+required slots that are still non_configured (missing) for this horse's current
// ration. Optional slots (coverage_required === false) and owner-managed slots never count as a gap.
export function coverageMissing(ration, activeSlots, setting) {
  return (activeSlots || [])
    .filter((s) => s.coverage_required !== false)
    .filter((s) => !slotOwnerManaged(setting, s.id))
    .filter((s) => slotState(ration, s.id) === SLOT_STATE.NONE)
    .map((s) => s.name);
}

// Editor groups from a ration: configured groups (from lines) + declared-empty groups (from empty_slots).
export function rationToGroups(ration, activeSlots) {
  const groups = linesToGroups(ration?.lines, activeSlots);
  const present = new Set(groups.map((g) => g.slot_id));
  (ration?.empty_slots || []).forEach((sid) => {
    if (!present.has(sid) && (activeSlots || []).some((s) => s.id === sid)) groups.push({ _id: uidLite(), slot_id: sid, items: [], empty: true });
  });
  return groups;
}

// Editor groups -> {lines, empty_slots}. Group with foods -> lines; group flagged empty & no foods
// -> empty_slots; group with no foods & not flagged -> dropped (stays non_configured). null != 0 kept.
export function groupsToPayload(groups) {
  const lines = []; const empty_slots = [];
  for (const g of groups || []) {
    const its = g.items || [];
    if (its.some((it) => !it.feed_item_id && (it.quantity !== "" || (it.note || "").trim()))) return { error: "Seleziona l'alimento in ogni riga compilata" };
    const filled = its.filter((it) => it.feed_item_id);
    if (filled.length) filled.forEach((it) => lines.push({ meal_slot_id: g.slot_id, feed_item_id: it.feed_item_id, quantity: it.quantity === "" ? null : Number(it.quantity), note: it.note || null }));
    else if (g.empty) empty_slots.push(g.slot_id);
  }
  return { lines, empty_slots };
}
// then item name. `collapsed` = product name equals its category (avoid "Fieno / Fieno" redundancy).
export function categoryBlocksForSlot(slotLines, itemsMap, categoryOrder) {
  const sorter = makeLineSorter(itemsMap, categoryOrder);
  const byCat = {};
  (slotLines || []).forEach((l) => { const c = itemsMap[l.feed_item_id]?.category || "Altro"; (byCat[c] = byCat[c] || []).push(l); });
  const cats = (categoryOrder || DEFAULT_CATEGORY_ORDER).filter((c) => byCat[c]);
  Object.keys(byCat).forEach((c) => { if (!cats.includes(c)) cats.push(c); });
  return cats.map((c) => ({
    category: c,
    entries: [...byCat[c]].sort(sorter).map((l) => {
      const it = itemsMap[l.feed_item_id]; const name = it?.name || "?";
      const qty = (l.quantity === null || l.quantity === undefined) ? "nessun dato" : fmtQty(l.quantity, it?.base_unit);
      return { name, qty, collapsed: name.trim().toLowerCase() === c.trim().toLowerCase(), color: it?.color || null, feed_item_id: l.feed_item_id };
    }),
  }));
}
